<template>
  <el-card>
    <div class="toolbar fund-toolbar">
      <el-input v-model="keyword" placeholder="账号/昵称" clearable style="width:180px" @keyup.enter.native="search"></el-input>
      <el-input v-model="userId" placeholder="用户ID" clearable style="width:120px;margin-left:8px" @keyup.enter.native="search"></el-input>
      <el-date-picker
        class="ml8"
        v-model="dateRange"
        type="daterange"
        unlink-panels
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        value-format="yyyy-MM-dd"
        style="width:260px"
        @change="onDateChange"
      ></el-date-picker>
      <el-button type="primary" class="ml8" @click="search">查询</el-button>
      <el-button type="success" class="ml8" @click="openExport">导出报表</el-button>
    </div>
    <div class="toolbar fund-date-presets">
      <el-button-group>
        <el-button
          v-for="item in presets"
          :key="item.key"
          size="mini"
          :type="preset === item.key ? 'primary' : 'default'"
          @click="applyPreset(item.key)"
        >{{ item.label }}</el-button>
      </el-button-group>
      <span v-if="bizRangeText" class="pnl-hint">实际范围 {{ bizRangeText }}</span>
    </div>
    <div class="pnl-bar fund-stats">
      <div class="stat-item">
        <span>流水合计</span>
        <b>{{ formatMoney(stats.flowTotal) }}</b>
      </div>
      <div class="stat-item">
        <span>发包合计</span>
        <b>{{ formatMoney(stats.sendTotal) }}</b>
      </div>
      <div class="stat-item">
        <span>抢包合计</span>
        <b>{{ formatMoney(stats.grabTotal) }}</b>
      </div>
      <div class="stat-item">
        <span>盈亏金额</span>
        <b :class="pnlClass">{{ formatPnl(stats.pnlTotal) }}</b>
      </div>
    </div>
    <div class="pnl-bar fund-stats fund-stats-sub">
      <div v-for="item in typeStats" :key="item.key" class="stat-item">
        <span>{{ item.label }}</span>
        <b>{{ formatMoney(stats[item.key]) }}</b>
      </div>
    </div>
    <el-table :data="list" v-loading="loading" border>
      <el-table-column prop="id" label="ID" width="70"></el-table-column>
      <el-table-column prop="userId" label="用户ID" width="80"></el-table-column>
      <el-table-column prop="username" label="账号" width="110"></el-table-column>
      <el-table-column prop="nickname" label="昵称" width="120"></el-table-column>
      <el-table-column prop="userRemark" label="用户备注" min-width="140">
        <template slot-scope="s">
          <span>{{ s.row.userRemark || '—' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="类型" width="120">
        <template slot-scope="s">{{ typeMap[s.row.type] || s.row.type }}</template>
      </el-table-column>
      <el-table-column prop="amount" label="变动金额" width="110"></el-table-column>
      <el-table-column prop="balanceAfter" label="变动后余额" width="120"></el-table-column>
      <el-table-column prop="remark" label="备注"></el-table-column>
      <el-table-column prop="createdAt" label="时间" width="170"></el-table-column>
    </el-table>
    <el-pagination
      style="margin-top:12px"
      background
      layout="total,sizes,prev,pager,next"
      :total="total"
      :page-sizes="[10, 20, 50, 100, 200]"
      :page-size="size"
      :current-page.sync="page"
      @size-change="onSizeChange"
      @current-change="load"
    ></el-pagination>
    <el-dialog title="导出资金报表" :visible.sync="exportDialog" width="480px">
      <el-form label-width="110px">
        <el-form-item label="开始日期时间" required>
          <el-date-picker
            v-model="exportStart"
            type="datetime"
            placeholder="选择开始时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            default-time="00:00:00"
            style="width:100%"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="结束日期时间" required>
          <el-date-picker
            v-model="exportEnd"
            type="datetime"
            placeholder="选择结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            default-time="23:59:59"
            style="width:100%"
          ></el-date-picker>
        </el-form-item>
      </el-form>
      <div class="pnl-hint">按所选时间汇总每个用户各资金类型合计，最后一列为盈亏（不含上分/下分）。</div>
      <div slot="footer">
        <el-button @click="exportDialog = false">取消</el-button>
        <el-button type="primary" :loading="exporting" @click="doExport">导出</el-button>
      </div>
    </el-dialog>
  </el-card>
</template>

<script>
import { funds as fetchFunds, fundStats as fetchFundStats, exportFundReport } from '../api'

const TYPE_MAP = {
  UP_SCORE: '上分',
  DOWN_SCORE: '下分',
  SEND: '发红包',
  GRAB: '抢红包',
  MINE_PAY: '中雷赔付',
  MINE_INCOME: '中雷收入',
  TIMEOUT_COMPENSATE: '尾雷赔付',
  GRAB_REWARD: '抢包奖励',
  SEND_REWARD: '发包奖励',
  REFUND: '未领完退回'
}

const EMPTY_STATS = {
  flowTotal: 0,
  sendTotal: 0,
  grabTotal: 0,
  pnlTotal: 0,
  upScoreTotal: 0,
  downScoreTotal: 0,
  minePayTotal: 0,
  mineIncomeTotal: 0,
  timeoutCompensateTotal: 0,
  grabRewardTotal: 0,
  sendRewardTotal: 0,
  refundTotal: 0
}

const TYPE_STATS = [
  { key: 'upScoreTotal', label: '上分' },
  { key: 'downScoreTotal', label: '下分' },
  { key: 'minePayTotal', label: '中雷赔付' },
  { key: 'mineIncomeTotal', label: '中雷收入' },
  { key: 'timeoutCompensateTotal', label: '尾雷赔付' },
  { key: 'grabRewardTotal', label: '抢包奖励' },
  { key: 'sendRewardTotal', label: '发包奖励' },
  { key: 'refundTotal', label: '未领完退回' }
]

const PRESETS = [
  { key: 'today', label: '今天' },
  { key: 'yesterday', label: '昨天' },
  { key: 'last3', label: '近三天' },
  { key: 'last7', label: '近7天' },
  { key: 'lastMonth', label: '近1个月' }
]

function pad(n) {
  return n < 10 ? '0' + n : '' + n
}

function formatDate(d) {
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
}

function addDays(d, n) {
  const next = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  next.setDate(next.getDate() + n)
  return next
}

function addMonths(d, n) {
  const next = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  next.setMonth(next.getMonth() + n)
  return next
}

function presetRange(key) {
  const today = new Date()
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  if (key === 'today') {
    return [formatDate(startOfToday), formatDate(startOfToday)]
  }
  if (key === 'yesterday') {
    const y = addDays(startOfToday, -1)
    return [formatDate(y), formatDate(y)]
  }
  if (key === 'last3') {
    return [formatDate(addDays(startOfToday, -2)), formatDate(startOfToday)]
  }
  if (key === 'last7') {
    return [formatDate(addDays(startOfToday, -6)), formatDate(startOfToday)]
  }
  if (key === 'lastMonth') {
    return [formatDate(addMonths(startOfToday, -1)), formatDate(startOfToday)]
  }
  return null
}

function sameRange(a, b) {
  return !!(a && b && a[0] === b[0] && a[1] === b[1])
}

export default {
  name: 'Funds',
  data() {
    return {
      loading: false,
      keyword: '',
      userId: '',
      dateRange: null,
      preset: '',
      presets: PRESETS,
      page: 1,
      size: 10,
      total: 0,
      list: [],
      typeMap: TYPE_MAP,
      typeStats: TYPE_STATS,
      stats: Object.assign({}, EMPTY_STATS),
      exportDialog: false,
      exporting: false,
      exportStart: '',
      exportEnd: ''
    }
  },
  computed: {
    pnlClass() {
      const n = Number(this.stats.pnlTotal)
      if (n > 0) return 'pnl-plus'
      if (n < 0) return 'pnl-minus'
      return 'pnl-flat'
    },
    bizRangeText() {
      if (!this.dateRange || this.dateRange.length !== 2 || !this.dateRange[0] || !this.dateRange[1]) {
        return ''
      }
      const endNext = formatDate(addDays(new Date(this.dateRange[1] + 'T00:00:00'), 1))
      return this.dateRange[0] + ' 06:00:00 至 ' + endNext + ' 06:00:00'
    }
  },
  created() {
    if (this.$route.query.userId) {
      this.userId = String(this.$route.query.userId)
    }
    this.load()
  },
  methods: {
    formatMoney(val) {
      const n = Number(val)
      return (Number.isFinite(n) ? n : 0).toFixed(2)
    },
    formatPnl(val) {
      const n = Number(val)
      const text = (Number.isFinite(n) ? n : 0).toFixed(2)
      return n > 0 ? '+' + text : text
    },
    applyPreset(key) {
      if (this.preset === key) {
        this.preset = ''
        this.dateRange = null
      } else {
        this.preset = key
        this.dateRange = presetRange(key)
      }
      this.search()
    },
    onDateChange(val) {
      this.dateRange = val && val.length === 2 ? val : null
      const matched = PRESETS.find((item) => sameRange(this.dateRange, presetRange(item.key)))
      this.preset = matched ? matched.key : ''
      this.search()
    },
    search() {
      this.page = 1
      this.load()
    },
    onSizeChange(val) {
      this.size = val
      this.page = 1
      this.load()
    },
    openExport() {
      if (this.dateRange && this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1]) {
        this.exportStart = this.dateRange[0] + ' 00:00:00'
        this.exportEnd = this.dateRange[1] + ' 23:59:59'
      } else {
        const today = formatDate(new Date())
        this.exportStart = today + ' 00:00:00'
        this.exportEnd = today + ' 23:59:59'
      }
      this.exportDialog = true
    },
    doExport() {
      if (!this.exportStart || !this.exportEnd) {
        this.$message.warning('请选择开始时间和结束时间')
        return
      }
      if (this.exportEnd < this.exportStart) {
        this.$message.warning('结束时间不能早于开始时间')
        return
      }
      this.exporting = true
      exportFundReport({
        startTime: this.exportStart,
        endTime: this.exportEnd
      }).then(() => {
        this.$message.success('导出成功')
        this.exportDialog = false
      }).catch((e) => {
        this.$message.error(e.message)
      }).then(() => {
        this.exporting = false
      })
    },
    filterParams() {
      const params = {
        keyword: this.keyword,
        userId: this.userId ? Number(this.userId) : undefined
      }
      if (this.dateRange && this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1]) {
        params.startDate = this.dateRange[0]
        params.endDate = this.dateRange[1]
      }
      return params
    },
    load() {
      this.loading = true
      const params = this.filterParams()
      Promise.all([
        fetchFunds(Object.assign({ page: this.page, size: this.size }, params)),
        fetchFundStats(params)
      ]).then(([data, stats]) => {
        this.list = data.records || []
        this.total = data.total || 0
        const next = Object.assign({}, EMPTY_STATS)
        if (stats) {
          Object.keys(next).forEach((key) => {
            if (stats[key] != null) next[key] = stats[key]
          })
        }
        this.stats = next
      }).catch((e) => {
        this.$message.error(e.message)
      }).then(() => {
        this.loading = false
      })
    }
  }
}
</script>
